<div class="ai">
	<!-- Top bar -->
	<div class="ai-hdr">
		<div>
			<h1>AI Agents</h1>
			<p>Orchestrate the LMS agent roster — enable, assign to campaigns, set autonomy &amp; schedule. Controls are live but local; dispatch &amp; telemetry land with the call-event feed.</p>
		</div>
	</div>

	<!-- Tab bar -->
	<div class="ai-tabs" role="tablist">
		<button class="ai-tab" *ngFor="let t of tabs" [class.active]="tab === t.key" (click)="setTab(t.key)">{{ t.label }}</button>
	</div>

	<!-- ─────────────────────────  TAB 1: LIVE FLOOR  ───────────────────────── -->
	<div *ngIf="tab === 'floor'" class="ai-panel">
		<div class="ai-floor-toolbar" *ngIf="finishedCount">
			<div class="form-check form-switch ai-floor-toggle" title="Finished instances stay listed so you can review their calls and results">
				<input class="form-check-input" type="checkbox" role="switch" id="aiHideFinished" [checked]="hideFinished" (change)="setHideFinished($any($event.target).checked)" />
				<label class="form-check-label" for="aiHideFinished">Hide finished <span class="ai-muted">({{ finishedCount }})</span></label>
			</div>
		</div>
		<table class="ai-table">
			<thead>
				<tr>
					<th>Agent</th><th class="ai-floor-center">Status</th><th>Phone</th><th>Campaigns</th><th class="ai-floor-center">Progress</th>
					<th>Now</th><th>Started</th>
					<th class="ai-floor-actions-hd"><button class="btn btn-sm btn-soft-secondary" (click)="refreshInstances()" title="Refresh">↻</button></th>
				</tr>
			</thead>
			<tbody>
				<tr *ngIf="!floorInstances.length">
					<td colspan="8" class="ai-floor-empty">
						<span class="ai-muted">{{ instancesLoaded ? 'No agents running right now.' : 'Loading…' }}</span>
						<p class="ai-muted" *ngIf="instancesLoaded && !finishedCount">Each Start on an agent's card (Agents tab) creates an instance that shows up here. It stays listed after its campaign queue is exhausted or it's stopped.</p>
						<p class="ai-muted" *ngIf="instancesLoaded && finishedCount">{{ finishedCount }} finished {{ finishedCount === 1 ? 'instance is' : 'instances are' }} hidden — uncheck "Hide finished" to see them.</p>
					</td>
				</tr>
				<tr *ngFor="let i of floorInstances" [class.ai-floor-finished]="i.status === 'finished'">
					<td>
						<div class="ai-floor-agent">
							<span class="ai-floor-icon" aria-hidden="true">{{ instanceAgent(i)?.icon }}</span>
							<div>
								<strong>{{ i.name }}</strong>
								<div class="ai-muted ai-floor-agent-id">#{{ i.id }}</div>
							</div>
						</div>
					</td>
					<td class="ai-floor-center">
						<span class="ai-badge" [class.ai-badge-success]="i.status === 'active'" [class.ai-badge-pending]="i.status === 'paused'" [class.ai-badge-danger]="i.status === 'finished'">{{ i.status === 'active' ? 'Active' : i.status === 'paused' ? 'Paused' : wasStopped(i) ? 'Stopped' : 'Finished' }}</span>
					</td>
					<td>{{ instancePhone(i) }}</td>
					<td>
						<div class="ai-floor-campaigns" *ngIf="i.campaigns?.length">
							<span>{{ i.campaigns[0] }}</span>
							<button *ngIf="i.campaigns.length > 1" type="button" class="btn ai-btn ai-btn-sm ai-btn-prospects" (click)="openCampaigns(i)" title="See every campaign this instance works, in order">+{{ i.campaigns.length - 1 }} more</button>
						</div>
						<span class="ai-muted" *ngIf="!i.campaigns?.length">—</span>
					</td>
					<td class="ai-floor-center">{{ i.queue_called }} / {{ i.queue_total }}</td>
					<td>
						<span *ngIf="i.status === 'finished'" class="ai-muted">{{ i.finished_at ? (i.finished_at | date: 'MMM d, h:mm a') : '—' }}</span>
						<ng-container *ngIf="i.status !== 'finished'" [ngSwitch]="i.last_action">
							<span *ngSwitchCase="'CALLING'">📞 Calling <strong>{{ i.last_prospect_name || 'Prospect #' + i.last_prospect_id }}</strong><span class="ai-muted" *ngIf="i.last_campaign_name"> · {{ i.last_campaign_name }}</span></span>
							<span *ngSwitchCase="'ERROR'" class="ai-floor-error">Last call failed<span *ngIf="i.last_prospect_name"> · {{ i.last_prospect_name }}</span></span>
							<span *ngSwitchCase="'END_CALL'" class="ai-muted">{{ i.status === 'paused' ? 'Last call ended' : 'Between calls' }}</span>
							<span *ngSwitchDefault class="ai-muted">—</span>
						</ng-container>
					</td>
					<td>{{ i.created_at | date: 'MMM d, h:mm a' }}</td>
					<td>
						<div class="ai-floor-actions">
							<button *ngIf="i.status === 'active'" class="btn ai-btn ai-btn-sm ai-btn-pause" [disabled]="instanceBusyId === +i.id" (click)="pauseInstance(i)" title="Doesn't cut the call in progress — only stops the next one from starting">⏸ Pause</button>
							<button *ngIf="i.status === 'paused'" class="btn ai-btn ai-btn-sm ai-btn-start" [disabled]="instanceBusyId === +i.id" (click)="resumeInstance(i)" title="Continues with the next pending prospect">▶ Resume</button>
							<button *ngIf="i.status !== 'finished'" class="btn ai-btn ai-btn-sm ai-btn-stop" [disabled]="instanceBusyId === +i.id" (click)="stopInstance(i)" title="Ends this instance for good and frees its phone number">⏹ Stop</button>
							<button class="btn ai-btn ai-btn-sm ai-btn-prospects" (click)="openProspects(i)">{{ isLeadAgent(instanceAgent(i)?.key) ? 'Leads' : 'Prospects' }}</button>
							<button class="btn ai-btn ai-btn-sm ai-btn-prospects" (click)="openCalls(instanceAgent(i), i)">Calls</button>
						</div>
					</td>
				</tr>
			</tbody>
		</table>
	</div>

	<!-- ─────────────────────────  TAB 2: AGENTS (default)  ───────────────────────── -->
	<div *ngIf="tab === 'agents'">
		<!-- Voice dialers -->
		<div class="ai-section-hd"><h2>Voice dialers</h2><span class="ai-muted">Outbound agents — each works its own ordered campaign queue, one campaign at a time. Everything (pause, campaigns, schedule) is configured from its card.</span></div>
		<div class="ai-roster">
			<div class="ai-card" *ngFor="let a of visibleVoiceAgents" [class.off]="!isEnabled(a.key)">
				<div class="ai-card-icon" aria-hidden="true">{{ a.icon }}</div>

				<div class="ai-card-body">
					<div class="ai-card-top">
						<div class="ai-card-heading">
							<span class="ai-name">{{ a.name }}</span>
							<span class="ai-tag ai-offhours" *ngIf="isOutsideSchedule(a.key)" title="Outside its configured call window — won't dispatch until the window opens">off-hours</span>
						</div>
					</div>

					<p class="ai-desc">{{ a.description }}</p>

					<div class="ai-current-call" *ngIf="instancesOf(a) as running">
						<ng-container *ngIf="running.length; else noInstances">
							<strong>{{ running.length }}</strong> {{ running.length === 1 ? 'instance' : 'instances' }} running —
							<a href="javascript:void(0)" (click)="setTab('floor')">see Live floor</a>
						</ng-container>
						<ng-template #noInstances><span class="ai-muted">No instances running</span></ng-template>
					</div>
				</div>

				<div class="ai-card-actions">
					<button class="btn ai-btn ai-btn-start" (click)="openConfig(a)" [disabled]="startingKey === a.key" title="Pick campaigns and a phone number, then start a new instance of this agent — it calls one prospect at a time, automatically chaining the next call until its campaign queue is exhausted">
						<span class="ai-btn-ico">▶</span> {{ startingKey === a.key ? 'Starting…' : 'Start' }}
					</button>
					<button class="btn ai-btn ai-btn-prospects" (click)="openCalls(a)">
						<span class="ai-btn-ico">📞</span> Calls
					</button>
				</div>
			</div>
		</div>

		<!-- Inbound -->
		<div class="ai-section-hd" *ngIf="showInboundAgents"><h2>Inbound</h2><span class="ai-muted">Receives calls — never dispatched outbound, no dial queue.</span></div>
		<div class="ai-roster" *ngIf="showInboundAgents">
			<div class="ai-card" *ngFor="let a of inboundAgents" [class.off]="!isEnabled(a.key)">
				<div class="ai-card-icon" aria-hidden="true">{{ a.icon }}</div>

				<div class="ai-card-body">
					<div class="ai-card-top">
						<div class="ai-card-heading">
							<span class="ai-name">{{ a.name }}</span>
							<span class="ai-tag">Inbound only</span>
						</div>
						<label class="ai-switch" [title]="isEnabled(a.key) ? 'Enabled' : 'Disabled'">
							<input type="checkbox" [checked]="isEnabled(a.key)" (change)="toggleEnabled(a.key)" />
							<span class="ai-switch-track"></span>
						</label>
					</div>
					<p class="ai-desc">{{ a.description }}</p>
					<div class="ai-assign-row"><span class="ai-muted">Receives calls · no dial queue</span></div>
					<div class="ai-ctl-row">
						<label class="ai-ctl">
							<span>Autonomy</span>
							<select class="ai-autosel" [value]="autonomyByAgent[a.key]" (change)="setAgentAutonomy(a.key, $any($event.target).value)">
								<option *ngFor="let m of autonomyModes" [value]="m">{{ m }}</option>
							</select>
						</label>
					</div>
				</div>
			</div>
		</div>

		<!-- Text agents (Make scenarios, separate row) -->
		<div class="ai-section-hd" *ngIf="showTextAgents"><h2>Text agents</h2><span class="ai-muted">Make scenarios — event-driven, no Vapi assistant, not dialable.</span></div>
		<div class="ai-roster" *ngIf="showTextAgents">
			<div class="ai-card" *ngFor="let a of textAgents" [class.off]="!isEnabled(a.key)">
				<div class="ai-card-icon" aria-hidden="true">{{ a.icon }}</div>

				<div class="ai-card-body">
					<div class="ai-card-top">
						<div class="ai-card-heading">
							<span class="ai-name">{{ a.name }}</span>
							<span class="ai-tag">Event-driven</span>
						</div>
						<label class="ai-switch" [title]="isEnabled(a.key) ? 'Enabled' : 'Disabled'">
							<input type="checkbox" [checked]="isEnabled(a.key)" (change)="toggleEnabled(a.key)" />
							<span class="ai-switch-track"></span>
						</label>
					</div>
					<p class="ai-desc">{{ a.description }}</p>
					<div class="ai-ctl-row">
						<label class="ai-ctl">
							<span>Autonomy</span>
							<select class="ai-autosel" [value]="autonomyByAgent[a.key]" (change)="setAgentAutonomy(a.key, $any($event.target).value)">
								<option *ngFor="let m of autonomyModes" [value]="m">{{ m }}</option>
							</select>
						</label>
					</div>
				</div>
			</div>
		</div>
	</div>

	<!-- ─────────────────────────  TAB 3: STATS  ───────────────────────── -->
	<div *ngIf="tab === 'stats'">
		<div class="ai-stats-bar">
			<span class="ai-launch-lbl">Reporting period</span>
			<div class="ai-period">
				<button class="ai-pbtn" *ngFor="let p of periods" [class.active]="period === p" (click)="setPeriod(p)">{{ p }}{{ p === 'monthly' ? ' (trend)' : '' }}</button>
			</div>
			<span class="ai-muted">KPIs mirror human-caller metrics.</span>
		</div>

		<!-- AI vs human comparison (gated) -->
		<div class="ai-panel">
			<div class="ai-scaffold-hd">AI vs human callers</div>
			<div class="ai-compare-empty">
				<div class="ai-compare-icon">📊</div>
				<div>
					<strong>Needs real volume.</strong> Most agents are talk-only until dispatch is wired, so a head-to-head vs human callers would be misleading. This unlocks once agents run live at volume.
					<div><span class="ai-pending">pending — needs call-event feed</span></div>
				</div>
			</div>
		</div>

		<!-- Per-agent KPI card shells -->
		<div class="ai-stat-grid">
			<div class="ai-panel ai-statcard" *ngFor="let a of visibleDialerAgents">
				<div class="ai-statcard-top">
					<div><strong>{{ a.name }}</strong> <span class="ai-muted">· {{ a.role }}</span></div>
					<span class="ai-pending" *ngIf="!statsByAgent[a.key]">no calls yet</span>
				</div>
				<div class="ai-kpis">
					<div class="ai-kpi">
						<span class="ai-kpi-v">{{ statsByAgent[a.key]?.dials ?? '—' }}</span><span class="ai-kpi-k">Dials</span>
					</div>
					<div class="ai-kpi">
						<span class="ai-kpi-v">{{ statsByAgent[a.key]?.dials && statsByAgent[a.key]?.connected != null ? ((statsByAgent[a.key].connected / statsByAgent[a.key].dials) * 100 | number: '1.0-0') + '%' : '—' }}</span><span class="ai-kpi-k">Connect rate</span>
					</div>
					<div class="ai-kpi">
						<span class="ai-kpi-v">{{ statsByAgent[a.key]?.talk_seconds ? (statsByAgent[a.key].talk_seconds / 60 | number: '1.0-1') + 'm' : '—' }}</span><span class="ai-kpi-k">Talk time</span>
					</div>
					<div class="ai-kpi">
						<span class="ai-kpi-v">—</span><span class="ai-kpi-k">Appts set</span>
					</div>
					<div class="ai-kpi">
						<span class="ai-kpi-v">—</span><span class="ai-kpi-k">Conversion</span>
					</div>
					<div class="ai-kpi">
						<span class="ai-kpi-v">{{ statsByAgent[a.key]?.total_cost ? ('$' + (statsByAgent[a.key].total_cost | number: '1.2-2')) : '—' }}</span><span class="ai-kpi-k">Cost</span>
					</div>
				</div>
				<div class="ai-muted fs-12" *ngIf="!statsByAgent[a.key]?.dials">Measured live from Vapi's own call analytics — appears once this assistant has placed a call.</div>
				<div class="ai-muted fs-12" *ngIf="statsByAgent[a.key]?.dials">Connect rate / appts set / conversion need the Vapi outcome schema, not wired yet.</div>
			</div>
		</div>
	</div>
</div>

<!-- Configure modal: pause/autonomy, campaign waterfall, schedule — everything for one dialer -->
<div class="ai-modal-wrap" *ngIf="configQueue as q">
	<div class="ai-modal-overlay" (click)="closeConfig()"></div>
	<div class="ai-modal ai-modal-lg">
		<div class="ai-modal-title">
			<span class="ai-modal-icon">{{ configAgent?.icon }}</span> Configure — {{ configAgent?.name }} <span class="ai-muted">· {{ q.name }}</span>
			<span class="ai-flex-spacer"></span>
			<button type="button" class="ai-modal-close" (click)="closeConfig()" aria-label="Close">✕</button>
		</div>

		<div class="ai-sched-alert" *ngIf="configAgent && isOutsideSchedule(configAgent.key)">
			⚠ Currently outside its call window (Pacific time) — this agent won't dispatch calls until its configured window below. Adjust it if you want it to run now.
		</div>

		<div class="ai-modal-body">
			<div class="ai-camps">
				<div class="ai-lbl">Campaign queue <span class="ai-muted">(order matters — it works the first one, and moves to the next once that one is exhausted)</span></div>

				<ng-container>
					<div class="ai-waterfall" *ngIf="formCampaignIds.length; else noCamps">
						<div class="ai-wf-row" *ngFor="let cid of formCampaignIds; let i = index">
							<span class="ai-wf-idx">{{ i + 1 }}</span>
							<span class="ai-wf-name">{{ campaignName(cid) }}</span>
							<span class="ai-tag ai-wf-active" *ngIf="isCampaignActive(q.id, cid, formCampaignIds)">active now</span>
							<span class="ai-tag ai-wf-done" *ngIf="campaignRemaining(q.id, cid) === false">exhausted</span>
							<span class="ai-wf-actions">
								<button class="ai-mini" (click)="moveFormCampaign(i, -1)" [disabled]="i === 0" title="Move up">▲</button>
								<button class="ai-mini" (click)="moveFormCampaign(i, 1)" [disabled]="i === formCampaignIds.length - 1" title="Move down">▼</button>
								<button class="ai-mini" (click)="removeFormCampaign(cid)" title="Remove">✕</button>
							</span>
						</div>
					</div>
					<ng-template #noCamps><div class="ai-muted">No campaigns assigned — this agent won't dispatch anything.</div></ng-template>

					<button type="button" class="btn btn-soft-primary ai-add-camp-btn" *ngIf="!campaignAddOpen" (click)="openCampaignAdd()">+ Add campaigns</button>
					<div class="ai-camp-picker" *ngIf="campaignAddOpen">
						<input #campaignSearchInput type="text" class="ai-sel" placeholder="Search a campaign to add…"
							[(ngModel)]="campaignSearch" (focus)="campaignPickerOpen = true" (blur)="onCampaignSearchBlur()" />
						<div class="ai-camp-picker-list" *ngIf="campaignPickerOpen">
							<button type="button" class="ai-camp-picker-item" *ngFor="let c of filteredCampaigns()" (mousedown)="$event.preventDefault(); addFormCampaign(c.id)">{{ c.name }}</button>
							<div class="ai-muted ai-camp-picker-empty" *ngIf="!filteredCampaigns().length">No results</div>
						</div>
					</div>
				</ng-container>
			</div>
			<app-available-phone-numbers [(selectedId)]="selectedPhoneNumberId" (loaded)="onPhoneNumbersLoaded($event)"></app-available-phone-numbers>
		</div>
		<div class="ai-modal-foot">
			<span class="ai-flex-spacer"></span>
			<button class="btn btn-primary" [disabled]="!!startingKey || !selectedPhoneNumberId || !formCampaignIds.length" (click)="saveCampaignQueue()">{{ startingKey ? 'Starting…' : 'Start Calling' }}</button>
		</div>
	</div>
</div>

<!-- Parker's exclusive Configure modal: one campaign or all of them + a start date, then a phone number — Start creates an instance like Ivy's -->
<div class="ai-modal-wrap" *ngIf="parkerConfigAgent as pa">
	<div class="ai-modal-overlay" (click)="closeParkerConfig()"></div>
	<div class="ai-modal ai-modal-lg">
		<div class="ai-modal-title">
			<span class="ai-modal-icon">{{ pa.icon }}</span> Configure — {{ pa.name }} <span class="ai-muted">· {{ pa.role }}</span>
			<span class="ai-flex-spacer"></span>
			<button type="button" class="ai-modal-close" (click)="closeParkerConfig()" aria-label="Close">✕</button>
		</div>

		<div class="ai-modal-body">
			<div class="ai-lbl">Campaigns</div>
			<div class="ai-mode-row">
				<label class="ai-mode-opt" [class.active]="parkerMode === 'all_campaigns'">
					<input type="radio" name="parker-mode" [checked]="parkerMode === 'all_campaigns'" (change)="setParkerMode('all_campaigns')" />
					All campaigns
				</label>
				<label class="ai-mode-opt" [class.active]="parkerMode === 'campaigns'">
					<input type="radio" name="parker-mode" [checked]="parkerMode === 'campaigns'" (change)="setParkerMode('campaigns')" />
					One campaign
				</label>
			</div>

			<div class="ai-camps" *ngIf="parkerMode === 'campaigns'">
				<div class="ai-lbl">Campaign</div>
				<div class="ai-waterfall" *ngIf="formCampaignIds.length">
					<div class="ai-wf-row">
						<span class="ai-wf-name">{{ campaignName(formCampaignIds[0]) }}</span>
						<span class="ai-wf-actions">
							<button class="ai-mini" (click)="formCampaignIds = []" title="Change campaign">✕</button>
						</span>
					</div>
				</div>
				<div class="ai-camp-picker" *ngIf="!formCampaignIds.length">
					<input #campaignSearchInput type="text" class="ai-sel" placeholder="Search a campaign…"
						[(ngModel)]="campaignSearch" (focus)="campaignPickerOpen = true" (blur)="onCampaignSearchBlur()" />
					<div class="ai-camp-picker-list" *ngIf="campaignPickerOpen">
						<button type="button" class="ai-camp-picker-item" *ngFor="let c of filteredCampaigns()" (mousedown)="$event.preventDefault(); setSingleCampaign(c.id)">{{ c.name }}</button>
						<div class="ai-muted ai-camp-picker-empty" *ngIf="!filteredCampaigns().length">No results</div>
					</div>
				</div>
			</div>

			<div class="ai-camps" *ngIf="parkerMode">
				<div class="ai-lbl">Start date <span class="ai-muted">(calls every lead whose meeting falls between this date and today)</span></div>
				<input type="date" class="ai-sel" [max]="today" [(ngModel)]="parkerStartDate" />
			</div>
			<app-available-phone-numbers [(selectedId)]="selectedPhoneNumberId" (loaded)="onPhoneNumbersLoaded($event)"></app-available-phone-numbers>
		</div>
		<div class="ai-modal-foot">
			<span class="ai-flex-spacer"></span>
			<button class="btn btn-primary" [disabled]="!canStartParker" (click)="saveParkerConfig()">{{ startingKey ? 'Starting…' : 'Start Calling' }}</button>
		</div>
	</div>
</div>

<!-- Renee's exclusive Configure modal: one campaign or all of them (no date), then a phone number — Start creates an instance like Ivy's -->
<div class="ai-modal-wrap" *ngIf="reneeConfigAgent as ra">
	<div class="ai-modal-overlay" (click)="closeReneeConfig()"></div>
	<div class="ai-modal ai-modal-lg">
		<div class="ai-modal-title">
			<span class="ai-modal-icon">{{ ra.icon }}</span> Configure — {{ ra.name }} <span class="ai-muted">· {{ ra.role }}</span>
			<span class="ai-flex-spacer"></span>
			<button type="button" class="ai-modal-close" (click)="closeReneeConfig()" aria-label="Close">✕</button>
		</div>

		<div class="ai-modal-body">
			<div class="ai-lbl">Campaigns <span class="ai-muted">(calls every lead pending a reschedule and offers 3 free slots from the campaign's calendar)</span></div>
			<div class="ai-mode-row">
				<label class="ai-mode-opt" [class.active]="reneeMode === 'all_campaigns'">
					<input type="radio" name="renee-mode" [checked]="reneeMode === 'all_campaigns'" (change)="setReneeMode('all_campaigns')" />
					All campaigns
				</label>
				<label class="ai-mode-opt" [class.active]="reneeMode === 'campaigns'">
					<input type="radio" name="renee-mode" [checked]="reneeMode === 'campaigns'" (change)="setReneeMode('campaigns')" />
					One campaign
				</label>
			</div>

			<div class="ai-camps" *ngIf="reneeMode === 'campaigns'">
				<div class="ai-lbl">Campaign</div>
				<div class="ai-waterfall" *ngIf="formCampaignIds.length">
					<div class="ai-wf-row">
						<span class="ai-wf-name">{{ campaignName(formCampaignIds[0]) }}</span>
						<span class="ai-wf-actions">
							<button class="ai-mini" (click)="formCampaignIds = []" title="Change campaign">✕</button>
						</span>
					</div>
				</div>
				<div class="ai-camp-picker" *ngIf="!formCampaignIds.length">
					<input #campaignSearchInput type="text" class="ai-sel" placeholder="Search a campaign…"
						[(ngModel)]="campaignSearch" (focus)="campaignPickerOpen = true" (blur)="onCampaignSearchBlur()" />
					<div class="ai-camp-picker-list" *ngIf="campaignPickerOpen">
						<button type="button" class="ai-camp-picker-item" *ngFor="let c of filteredCampaigns()" (mousedown)="$event.preventDefault(); setSingleCampaign(c.id)">{{ c.name }}</button>
						<div class="ai-muted ai-camp-picker-empty" *ngIf="!filteredCampaigns().length">No results</div>
					</div>
				</div>
			</div>
			<app-available-phone-numbers [(selectedId)]="selectedPhoneNumberId" (loaded)="onPhoneNumbersLoaded($event)"></app-available-phone-numbers>
		</div>
		<div class="ai-modal-foot">
			<span class="ai-flex-spacer"></span>
			<button class="btn btn-primary" [disabled]="!canStartRenee" (click)="saveReneeConfig()">{{ startingKey ? 'Starting…' : 'Start Calling' }}</button>
		</div>
	</div>
</div>

<!-- Campaigns modal — every campaign of one instance, in the order it works them -->
<div class="ai-modal-wrap" *ngIf="campaignsModal as cm">
	<div class="ai-modal-overlay" (click)="closeCampaigns()"></div>
	<div class="ai-modal">
		<div class="ai-modal-title">
			<span class="ai-modal-icon">{{ cm.icon }}</span> Campaigns — {{ cm.title }}
			<span class="ai-flex-spacer"></span>
			<button type="button" class="ai-modal-close" (click)="closeCampaigns()" aria-label="Close">✕</button>
		</div>
		<div class="ai-modal-body">
			<ol class="ai-floor-campaign-list">
				<li *ngFor="let c of cm.campaigns">{{ c }}</li>
			</ol>
		</div>
	</div>
</div>

<!-- Prospects modal: the queue of one instance — prospects (ai_agents_queue), or leads for Parker (ai_parker_queue) and Renee (ai_queue_renee) -->
<div class="ai-modal-wrap" *ngIf="prospectsAgent as pa">
	<div class="ai-modal-overlay" (click)="closeProspects()"></div>
	<div class="ai-modal" [class.ai-modal-lg]="!isLeadAgent(pa.key)" [class.ai-modal-xl]="pa.key === 'parker'" [class.ai-modal-xxl]="pa.key === 'renee'">
		<div class="ai-modal-title">
			<span class="ai-modal-icon">{{ pa.icon }}</span> {{ isLeadAgent(pa.key) ? 'Leads' : 'Prospects' }} — {{ prospectsTitle }}
			<span class="ai-flex-spacer"></span>
			<button type="button" class="ai-modal-close" (click)="closeProspects()" aria-label="Close">✕</button>
		</div>

		<div class="ai-modal-body">
			<div class="ai-prospects-loading" *ngIf="prospectsLoading">
				<span class="ai-spinner"></span> Loading {{ isLeadAgent(pa.key) ? 'leads' : 'prospects' }}…
			</div>

			<ng-container *ngIf="!prospectsLoading">
				<!-- Parker works leads: its queue is ai_parker_queue, shown column by column -->
				<!-- Renee works leads too (ai_queue_renee): original meeting + the 3 reschedule options she offered -->
				<ng-container *ngIf="pa.key === 'renee'">
					<div class="ai-table-scroll" *ngIf="prospects.length; else noLeads">
						<table class="ai-table ai-table-wide">
							<thead>
								<tr>
									<th>Lead ID</th><th>Campaign</th><th>Original meeting</th><th>Prospect</th><th>Company</th><th>Phone</th>
									<th>Option 1</th><th>Option 2</th><th>Option 3</th><th>Called</th><th>Called date</th>
								</tr>
							</thead>
							<tbody>
								<tr *ngFor="let l of prospects">
									<td>#{{ l.lead_id }}</td>
									<td>{{ l.campaign_name ?? (l.campaign_id ? '#' + l.campaign_id : '—') }}</td>
									<td>{{ l.original_meeting_date ? (l.original_meeting_date | date: 'MMM d, y, h:mm a') : '—' }}</td>
									<td>{{ l.prospect_name || '—' }}</td>
									<td>{{ l.company_name || '—' }}</td>
									<td>{{ l.phone_number ? (l.phone_number | phone) : '—' }}</td>
									<td>{{ l.proposed_date_1 ? (l.proposed_date_1 | date: 'EEE MMM d, h:mm a') : '—' }}</td>
									<td>{{ l.proposed_date_2 ? (l.proposed_date_2 | date: 'EEE MMM d, h:mm a') : '—' }}</td>
									<td>{{ l.proposed_date_3 ? (l.proposed_date_3 | date: 'EEE MMM d, h:mm a') : '—' }}</td>
									<td>
										<span class="ai-badge" [class.ai-badge-done]="l.called == 1" [class.ai-badge-pending]="l.called != 1">
											{{ l.called == 1 ? 'Called' : 'Pending' }}
										</span>
									</td>
									<td>{{ l.called_date ? (l.called_date | date: 'MMM d, y, h:mm a') : '—' }}</td>
								</tr>
							</tbody>
						</table>
					</div>
				</ng-container>

				<ng-container *ngIf="pa.key === 'parker'">
					<table class="ai-table" *ngIf="prospects.length; else noLeads">
						<thead>
							<tr>
								<th>Lead ID</th><th>Campaign</th><th>Meeting Date</th><th>Prospect</th><th>Company</th><th>Phone</th><th>Called</th><th>Called date</th>
							</tr>
						</thead>
						<tbody>
							<tr *ngFor="let l of prospects">
								<td>#{{ l.lead_id }}</td>
								<td>{{ l.campaign_name ?? (l.campaign_id ? '#' + l.campaign_id : '—') }}</td>
								<td>{{ l.date_generated ? (l.date_generated | date: 'MMM d, y, h:mm a') : '—' }}</td>
								<td>{{ l.prospect_name || '—' }}</td>
								<td>{{ l.company_name || '—' }}</td>
								<td>{{ l.phone_number ? (l.phone_number | phone) : '—' }}</td>
								<td>
									<span class="ai-badge" [class.ai-badge-done]="l.called == 1" [class.ai-badge-pending]="l.called != 1">
										{{ l.called == 1 ? 'Called' : 'Pending' }}
									</span>
								</td>
								<td>{{ l.called_date ? (l.called_date | date: 'MMM d, y, h:mm a') : '—' }}</td>
							</tr>
						</tbody>
					</table>
				</ng-container>
				<ng-template #noLeads><div class="ai-muted">No leads queued for this instance.</div></ng-template>

				<ng-container *ngIf="!isLeadAgent(pa.key)">
					<table class="ai-table" *ngIf="prospects.length; else noProspects">
						<thead>
							<tr>
								<th>Prospect ID</th><th>Name</th><th>Phone</th><th>Company</th><th>Called</th>
							</tr>
						</thead>
						<tbody>
							<tr *ngFor="let p of prospects">
								<td>{{ p.prospect_id }}</td>
								<td>{{ p.name }}</td>
								<td>{{ p.primary_phone }}</td>
								<td>{{ p.prospect_company }}</td>
								<td>
									<span class="ai-badge" [class.ai-badge-done]="p.called == 1" [class.ai-badge-pending]="p.called != 1">
										{{ p.called == 1 ? 'Called' : 'Pending' }}
									</span>
								</td>
							</tr>
						</tbody>
					</table>
					<ng-template #noProspects><div class="ai-muted">No prospects queued for this agent.</div></ng-template>
				</ng-container>
			</ng-container>
		</div>
	</div>
</div>

<!-- Calls modal: full call log for one agent (ai_calls, joined via vapi_assistant_id) -->
<div class="ai-modal-wrap" *ngIf="callsAgent as ca">
	<div class="ai-modal-overlay" (click)="closeCalls()"></div>
	<div class="ai-modal" [class.ai-modal-xl]="!isLeadAgent(ca.key)" [class.ai-modal-xxl]="isLeadAgent(ca.key)">
		<div class="ai-modal-title">
			<span class="ai-modal-icon">{{ ca.icon }}</span> Calls — {{ callsTitle }}
			<span class="ai-flex-spacer"></span>
			<button type="button" class="ai-modal-close" (click)="closeCalls()" aria-label="Close">✕</button>
		</div>

		<div class="ai-modal-body">
			<div class="ai-prospects-loading" *ngIf="callsLoading">
				<span class="ai-spinner"></span> Loading calls…
			</div>

			<ng-container *ngIf="!callsLoading">
				<div class="ai-table-scroll" *ngIf="calls.length; else noCalls">
					<table class="ai-table" [class.ai-table-wide]="isLeadAgent(ca.key)">
						<thead>
							<tr>
								<th>{{ isLeadAgent(ca.key) ? 'Lead' : 'Prospect' }}</th><th>Name</th><th>Company</th><th>Phone</th><th>Campaign</th>
								<th>Cost</th><th>Started</th>
								<th *ngIf="ca.key === 'ivy'">Interested</th>
								<ng-container *ngIf="ca.key === 'parker'">
									<th>Reached</th><th>Meeting held</th><th>No-show</th><th>Sentiment</th><th>Negative reason</th><th>Summary</th>
								</ng-container>
								<ng-container *ngIf="ca.key === 'renee'">
									<th>Reschedule</th><th>Option</th><th>New date</th><th>Sentiment</th><th>Summary</th>
								</ng-container>
								<th>Transcript</th>
							</tr>
						</thead>
						<tbody>
							<tr *ngFor="let c of calls">
								<td>{{ (c.lead_id ?? c.prospect_id) ? '#' + (c.lead_id ?? c.prospect_id) : '—' }}</td>
								<td>{{ c.prospect_name ?? '—' }}</td>
								<td>{{ c.company_name ?? '—' }}</td>
								<td>{{ c.phone_number ? (c.phone_number | phone) : '—' }}</td>
								<td>{{ c.campaign_id ? campaignName(c.campaign_id) : '—' }}</td>
								<td>{{ c.cost != null ? ('$' + c.cost) : '—' }}</td>
								<td>{{ c.started_at ? (c.started_at | date: 'MMM d, y, h:mm a') : '—' }}</td>
								<td *ngIf="ca.key === 'ivy'">
									<span class="ai-badge" *ngIf="prospectInterest(c) as pi; else noInterest"
										[class.ai-badge-danger]="pi.level === 'bad'"
										[class.ai-badge-warning]="pi.level === 'medium'"
										[class.ai-badge-success]="pi.level === 'interested'">{{ pi.label }}</span>
									<ng-template #noInterest>—</ng-template>
								</td>
								<ng-container *ngIf="ca.key === 'parker'">
									<ng-container *ngIf="parkerOutcome(c) as po">
										<td>
											<span class="ai-badge" *ngIf="po.reached as r; else dash"
												[class.ai-badge-success]="r.level === 'success'"
												[class.ai-badge-danger]="r.level === 'danger'">{{ r.label }}</span>
										</td>
										<td>
											<span class="ai-badge" *ngIf="po.meeting as m; else dash"
												[class.ai-badge-success]="m.level === 'success'"
												[class.ai-badge-danger]="m.level === 'danger'">{{ m.label }}</span>
										</td>
										<td>{{ po.noShow ?? '—' }}</td>
										<td>
											<span class="ai-badge" *ngIf="po.sentiment as st; else dash"
												[class.ai-badge-success]="st.level === 'success'"
												[class.ai-badge-warning]="st.level === 'warning'"
												[class.ai-badge-danger]="st.level === 'danger'">{{ st.label }}</span>
										</td>
										<td class="ai-cell-text">{{ po.negativeReason ?? '—' }}</td>
										<td class="ai-cell-text ai-cell-summary">{{ po.summary ?? '—' }}</td>
									</ng-container>
								</ng-container>
								<ng-container *ngIf="ca.key === 'renee'">
									<ng-container *ngIf="reneeOutcome(c) as ro">
										<td>
											<span class="ai-badge" *ngIf="ro.status as rs; else dash"
												[class.ai-badge-success]="rs.level === 'success'"
												[class.ai-badge-warning]="rs.level === 'warning'"
												[class.ai-badge-danger]="rs.level === 'danger'">{{ rs.label }}</span>
										</td>
										<td>{{ ro.option ?? '—' }}</td>
										<td>
											<div class="ai-resched" *ngIf="canApproveReschedule(c); else noResched">
												<input type="datetime-local" class="ai-sel" [ngModel]="rescheduleDateFor(c)" (ngModelChange)="setRescheduleDate(c, $event)"
													[disabled]="isLeadRescheduled(c) || rescheduleBusyId === +c.id" />
												<span class="ai-badge ai-badge-success" *ngIf="isLeadRescheduled(c); else approveBtn">Rescheduled</span>
												<ng-template #approveBtn>
													<button type="button" class="btn ai-btn ai-btn-sm ai-btn-start" [disabled]="!rescheduleDateFor(c) || rescheduleBusyId === +c.id" (click)="approveReschedule(c)">
														{{ rescheduleBusyId === +c.id ? 'Saving…' : 'Approve reschedule' }}
													</button>
												</ng-template>
											</div>
											<ng-template #noResched>{{ ro.newDate ?? '—' }}</ng-template>
										</td>
										<td>
											<span class="ai-badge" *ngIf="ro.sentiment as rst; else dash"
												[class.ai-badge-success]="rst.level === 'success'"
												[class.ai-badge-warning]="rst.level === 'warning'"
												[class.ai-badge-danger]="rst.level === 'danger'">{{ rst.label }}</span>
										</td>
										<td class="ai-cell-text ai-cell-summary">{{ ro.summary ?? '—' }}</td>
									</ng-container>
								</ng-container>
								<td><button class="btn btn-sm btn-soft-secondary" (click)="openCallDetail(c)">Transcript</button></td>
							</tr>
						</tbody>
					</table>
				</div>
				<ng-template #dash>—</ng-template>
				<ng-template #noCalls><div class="ai-muted">No calls logged for this agent yet.</div></ng-template>
			</ng-container>
		</div>
	</div>
</div>

<!-- Call detail modal: recording player (native HTML5 controls) on top, transcript chat below -->
<div class="ai-modal-wrap" *ngIf="callDetail as cd">
	<div class="ai-modal-overlay" (click)="closeCallDetail()"></div>
	<div class="ai-modal ai-modal-lg">
		<div class="ai-modal-title">
			{{ cd.prospect_name ?? 'Unknown prospect' }} <span class="ai-muted" *ngIf="cd.company_name">· {{ cd.company_name }}</span>
			<span class="ai-flex-spacer"></span>
			<button type="button" class="ai-modal-close" (click)="closeCallDetail()" aria-label="Close">✕</button>
		</div>

		<div class="ai-modal-body">
			<div class="ai-prospects-loading" *ngIf="recordingLoading">
				<span class="ai-spinner"></span> Fetching recording URL…
			</div>
			<ng-container *ngIf="!recordingLoading">
				<audio class="ai-native-player" *ngIf="recordingUrl" [src]="recordingUrl" controls></audio>
				<div class="ai-muted" *ngIf="!recordingUrl">No recording available for this call.</div>
			</ng-container>
			<div class="ai-muted ai-call-meta">
				<span>Vapi call ID: {{ cd.vapi_call_id ?? '—' }}</span>
				<span>{{ cd.started_at ? (cd.started_at | date: 'MMM d, y, h:mm a') : '—' }}</span>
			</div>

			<div class="ai-lbl ai-transcript-lbl">Transcript</div>
			<div class="ai-chat" *ngIf="transcriptMessages.length; else noTranscript">
				<div class="ai-chat-row" *ngFor="let m of transcriptMessages" [class.user]="m.role === 'user'">
					<div class="ai-chat-bubble" [class.bot]="m.role === 'bot'" [class.user]="m.role === 'user'">{{ m.message }}</div>
				</div>
			</div>
			<ng-template #noTranscript><div class="ai-muted">No transcript available for this call.</div></ng-template>
		</div>
	</div>
</div>

<div class="ai-toast" *ngIf="toast">{{ toast }}</div>
